import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";

// Module-level singleton state shared across all useAuth() consumers.
// Dzięki temu wejście na /panel czy /reset-hasla nie powoduje podwójnego
// "loading" (każdy komponent korzysta z tej samej, raz zainicjalizowanej sesji).
type AuthState = {
  user: User | null;
  isAdmin: boolean;
  loading: boolean;
};

let state: AuthState = { user: null, isAdmin: false, loading: true };
const listeners = new Set<(s: AuthState) => void>();
let initialized = false;

const setState = (patch: Partial<AuthState>) => {
  state = { ...state, ...patch };
  listeners.forEach((l) => l(state));
};

const checkAdminRole = async (userId: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "admin")
      .maybeSingle();
    if (error) {
      console.error("[useAuth] admin role query error:", error);
      return false;
    }
    return data?.role === "admin";
  } catch (e) {
    console.error("[useAuth] checkAdminRole exception:", e);
    return false;
  }
};

const initAuth = () => {
  if (initialized) return;
  initialized = true;

  let initDone = false;

  supabase.auth.onAuthStateChange((event, session) => {
    const user = session?.user ?? null;
    setState({ user });

    if (user) {
      // Defer the RPC, żeby uniknąć deadlocku w callbacku.
      setTimeout(async () => {
        const adminStatus = await checkAdminRole(user.id);
        setState({ isAdmin: adminStatus });
      }, 0);
    } else {
      setState({ isAdmin: false });
    }

    if (!initDone) {
      initDone = true;
      setState({ loading: false });
    }
  });

  // Fallback timeout
  setTimeout(() => {
    if (!initDone) {
      initDone = true;
      setState({ loading: false });
    }
  }, 3000);

  supabase.auth.getSession().then(async ({ data: { session } }) => {
    if (initDone) return;
    const user = session?.user ?? null;
    setState({ user });
    if (user) {
      const adminStatus = await checkAdminRole(user.id);
      setState({ isAdmin: adminStatus });
    }
    initDone = true;
    setState({ loading: false });
  }).catch(() => {
    if (!initDone) {
      initDone = true;
      setState({ loading: false });
    }
  });
};

export const useAuth = () => {
  const [snapshot, setSnapshot] = useState<AuthState>(state);

  useEffect(() => {
    initAuth();
    const listener = (s: AuthState) => setSnapshot(s);
    listeners.add(listener);
    // Sync in case state changed between render and effect.
    setSnapshot(state);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    // Auto-clear must_set_password: jeśli ktoś loguje się hasłem, to znaczy że
    // hasło istnieje. Bez tego user z resztkową flagą (np. zaproszony, później
    // zarejestrowany przez /rejestracja) trafiałby w nieskończoną pętlę
    // redirect do /panel/ustaw-haslo.
    try {
      const mustSet = (data?.user?.app_metadata as any)?.must_set_password === true;
      if (mustSet) {
        await supabase.functions.invoke("clear-must-set-password", { body: {} });
        await supabase.auth.refreshSession();
      }
    } catch (e) {
      console.warn("[signIn] clear must_set_password failed", e);
    }
  };

  const signUp = async (email: string, password: string, redirectPath?: string) => {
    const safePath = redirectPath && redirectPath.startsWith("/") && !redirectPath.startsWith("//")
      ? redirectPath
      : "/panel";
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: `${window.location.origin}${safePath}` },
    });
    if (error) throw error;
  };

  const sendPasswordResetEmail = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-hasla`,
    });
    if (error) throw error;
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
    // Wyloguj inne aktywne sesje (np. inne urządzenia) - bieżąca sesja zostaje
    try {
      await supabase.auth.signOut({ scope: "others" });
    } catch (e) {
      console.warn("[updatePassword] nie udało się wylogować innych sesji", e);
    }
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut({ scope: "local" });
    if (error) throw error;
  };

  return {
    user: snapshot.user,
    isAdmin: snapshot.isAdmin,
    loading: snapshot.loading,
    signIn,
    signUp,
    signOut,
    sendPasswordResetEmail,
    updatePassword,
  };
};
